import React, { useState, useEffect } from 'react';
import { FiPlus, FiCalendar, FiClock, FiTrash2, FiSearch, FiCheckCircle, FiXCircle, FiFilter, FiEdit, FiInfo, FiChevronLeft, FiChevronRight, FiChevronDown, FiChevronUp, FiFileText, FiTag, FiRefreshCw, FiBriefcase, FiTrendingUp } from 'react-icons/fi';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useUser } from '../../contexts/UserContext';
import { motion, AnimatePresence } from 'framer-motion';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// Format date as YYYY-MM-DD using local time (avoids UTC timezone shift)
const toInputDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Format date as DD/MM/YYYY
const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
};

const LeaveManagement = ({ initialStats, onStatsUpdate }) => {
    const { user } = useUser();

    // Role Definitions
    const roleId = Number(user?.user_role_id ?? user?.role_id);
    const isAdmin = roleId === 1;
    const isHR = user?.designation_id == 16 && roleId == 1;
    const isLeadOrAdmin = [1, 11].includes(roleId) || user?.designation_id == 16;
    const canViewAllHistory = isAdmin || isHR;
    const canAccessHistoryTab = isLeadOrAdmin;

    // Stats State
    const [stats, setStats] = useState(initialStats || {
        total_entitled: 12, leaves_taken: 0, leave_balance: 0, pending_requests: 0,
        pending_approvals: 0, pending_inquiries: 0, total_actionable: 0,
        min_date: new Date().toISOString().split('T')[0]
    });
    const [isAdminStats, setIsAdminStats] = useState(false);
    const [adminStats, setAdminStats] = useState({
        total_requests: 0, requested_count: 0, approved_count: 0, pending_count: 0, rejected_count: 0, type_dist: [], total_time_off: 0
    });
    const [statusChartSelection, setStatusChartSelection] = useState(null);
    const [typeChartSelection, setTypeChartSelection] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

    // Interaction State
    const [activeTab, setActiveTab] = useState(isAdmin ? 'approvals' : 'my_leaves');
    const [leaves, setLeaves] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 5, last_page: 1 });
    const [pendingApprovals, setPendingApprovals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [syncingAll, setSyncingAll] = useState(false);

    const handleSyncAllLeaves = async () => {
        if (!window.confirm('Recalculate leave balances for ALL employees? This may take a moment.')) return;
        setSyncingAll(true);
        try {
            const res = await api.get('hrms/sync-all-leaves');
            toast.success(res.data?.message || 'All leave balances recalculated successfully!');
            // Refresh current user stats after sync
            if (typeof onStatsUpdate === 'function') onStatsUpdate();
        } catch (e) {
            toast.error('Failed to sync leave balances.');
        } finally {
            setSyncingAll(false);
        }
    };

    // Initial Tab Selection & Deep Link Effect
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        let linkedLeaveId = params.get('leave_id');
        if (!linkedLeaveId) {
            const tabParam = params.get('tab') || '';
            if (tabParam.startsWith('leaves') && tabParam !== 'leaves') {
                linkedLeaveId = tabParam.replace('leaves', '');
            }
        }

        if (linkedLeaveId) {
            // If deep linked, prioritize finding the request
            if (isLeadOrAdmin) {
                setActiveTab('approvals');
            } else {
                setActiveTab('my_leaves');
            }
            // Optionally clear date filters to ensure visibility
            setFilters(prev => ({
                ...prev,
                startDate: '',
                endDate: '',
                status: 'All'
            }));
            // Highlight or Search for it
            setSearchTerm(linkedLeaveId); // Assuming search supports ID? 
            // The search backend (line 979/991) supports text search on Name/Reason/Type. 
            // It might NOT support ID search directly in 'search' param?
            // PHP: `->like('u.first_name', $search)...`
            // It does NOT seem to search by ID.

            // However, we can just highlight it visually if it's in the list.
        } else {
            // Default behavior: admin goes to approvals, not history
            if (isAdmin && activeTab === 'my_leaves') {
                setActiveTab('approvals');
            }
        }
    }, [canViewAllHistory, isLeadOrAdmin, window.location.search]);

    // Search and Pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);

    const todayStr = toInputDate(new Date());
    const firstDayOfMonth = toInputDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const lastDayOfMonth = toInputDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));

    const [filters, setFilters] = useState({
        startDate: firstDayOfMonth,
        endDate: lastDayOfMonth,
        status: 'All',
        type: '',
        employee_id: ''
    });

    // Reset page on filter change
    useEffect(() => {
        setCurrentPage(1);
    }, [filters, activeTab, searchTerm, sortConfig]);

    // List Logic (Server-side)
    const paginatedLeaves = leaves;
    const totalPages = meta.last_page || 1;
    // Chunks of 3 for page numbers
    const currentChunk = Math.ceil(currentPage / 3);
    const startPageCount = (currentChunk - 1) * 3 + 1;
    const visiblePages = [];
    for (let i = startPageCount; i < startPageCount + 3 && i <= totalPages; i++) {
        visiblePages.push(i);
    }

    // Dropdowns
    const [types, setTypes] = useState([]);
    const [employees, setEmployees] = useState([]);

    // Modals
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    // Rejection Modal State
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [selectedLeaveId, setSelectedLeaveId] = useState(null);

    const [showInquiryModal, setShowInquiryModal] = useState(false);
    const [inquiryStatusNote, setInquiryStatusNote] = useState('');
    const [currentInquiryId, setCurrentInquiryId] = useState(null);
    const [inquiryMode, setInquiryMode] = useState('Inquiry');
    const [currentInquiryThread, setCurrentInquiryThread] = useState('');
    const [permissionAnalytics, setPermissionAnalytics] = useState({ permission_hours: 0 });

    const [altSearch, setAltSearch] = useState('');
    const [showAltDropdown, setShowAltDropdown] = useState(false);
    const [holidays, setHolidays] = useState([]);

    const fetchHolidays = async (year) => {
        try {
            const y = year || new Date().getFullYear();
            // Fetch ALL holidays for the entire year so isHoliday() works for any selected month
            const res = await api.get('/hrms/calendar-events', { params: { year: y, year_only: 1 } });
            if (res.data.status) setHolidays(res.data.data.filter(e => e.event_type === 'Holiday'));
        } catch (e) { }
    };

    // Generic Alert/Confirm State
    const [alertConfig, setAlertConfig] = useState({
        show: false,
        title: 'Notification',
        message: '',
        confirmText: 'OK',
        cancelText: '',
        onConfirm: null,
        onCancel: null
    });

    const showAlert = (message, title = 'Notification', onConfirm = null) => {
        setAlertConfig({
            show: true,
            title,
            message,
            confirmText: 'OK',
            cancelText: '',
            onConfirm: () => {
                setAlertConfig(prev => ({ ...prev, show: false }));
                if (onConfirm) onConfirm();
            },
            onCancel: null
        });
    };

    const showConfirm = (message, title = 'Confirm Action', onConfirm, onCancel = null) => {
        setAlertConfig({
            show: true,
            title,
            message,
            confirmText: 'Continue',
            cancelText: 'Cancel',
            onConfirm: () => {
                setAlertConfig(prev => ({ ...prev, show: false }));
                if (onConfirm) onConfirm();
            },
            onCancel: () => {
                setAlertConfig(prev => ({ ...prev, show: false }));
                if (onCancel) onCancel();
            }
        });
    };

    const isHoliday = (dateStr) => {
        if (!dateStr) return { holiday: false };
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return { holiday: false };

        const cal = holidays.find(h => h.event_date === dateStr);
        if (cal) return { holiday: true, title: cal.event_title };
        return { holiday: false };
    };

    const getHolidayBlock = (dateStr) => {
        if (!dateStr || isNaN(new Date(dateStr).getTime())) return [];
        let block = [];
        let b = new Date(dateStr);
        b.setDate(b.getDate() - 1);
        while (isHoliday(b.toISOString().split('T')[0]).holiday) {
            block.unshift(b.toISOString().split('T')[0]);
            b.setDate(b.getDate() - 1);
        }
        let f = new Date(dateStr);
        f.setDate(f.getDate() + 1);
        while (isHoliday(f.toISOString().split('T')[0]).holiday) {
            block.push(f.toISOString().split('T')[0]);
            f.setDate(f.getDate() + 1);
        }
        return block;
    };

    const calculateNetDays = (startDate, endDate, isHalf = false) => {
        if (!startDate || !endDate) return 0;
        if (isHalf) return 0.5;
        let count = 0;
        let current = new Date(startDate);
        const end = new Date(endDate);
        let safety = 0;
        while (current <= end && safety < 100) {
            const dStr = current.toISOString().split('T')[0];
            if (!isHoliday(dStr).holiday) {
                count++;
            }
            current.setDate(current.getDate() + 1);
            safety++;
        }
        return count;
    };

    // Calculate Today's Date for Default Form State
    const today = new Date().toISOString().split('T')[0];

    const [form, setForm] = useState({
        leave_type_id: '',
        from_date: today,
        to_date: today,
        from_time: '',
        to_time: '',
        reason: '',
        is_emergency: false,
        compensate_date: '',
        late_reason: '',
        alternate_arrangement: false,
        alternate_user_id: [],
        alternate_work_details: '',
        is_half_day: false,
        half_day_timing: 'Forenoon'
    });

    // Dynamic shift timing for half-day labels (fetched based on employee + date)
    const [halfDayShift, setHalfDayShift] = useState(null);

    useEffect(() => {
        const isHalfDay = types.find(t => t.id == form.leave_type_id)?.code === 'HD';
        if (isHalfDay && form.from_date) {
            // Use the leave-applying user's ID (self) — admin leaves use their own shift
            const userId = user?.id || user?.user_id;
            if (userId) {
                api.get('/attendance/shift-for-date', { params: { user_id: userId, date: form.from_date } })
                    .then(r => setHalfDayShift(r.data?.shift || null))
                    .catch(() => setHalfDayShift(null));
            }
        }
    }, [form.leave_type_id, form.from_date]);

    // Helper: Format Time to 12h AM/PM
    const formatTime = (timeStr) => {
        if (!timeStr) return null;
        const [hours, minutes] = timeStr.split(':');
        let h = parseInt(hours, 10);
        const ampm = h >= 12 ? 'PM' : 'AM';
        h = h % 12;
        h = h ? h : 12;
        return `${h}:${minutes} ${ampm}`;
    };

    // Derived Form Values
    const selectedType = Array.isArray(types) ? types.find(t => t.id == form.leave_type_id) : null;
    const isPermission = selectedType?.code === 'PER';
    const isCCL = selectedType?.code === 'CCL';
    const isCL = selectedType?.code === 'CL';
    const isOD = selectedType?.code === 'OD';

    useEffect(() => {
        fetchTypes();
        fetchEmployees();
        fetchPendingApprovals();
        const year = form.from_date ? new Date(form.from_date).getFullYear() : new Date().getFullYear();
        fetchHolidays(year);
    }, [showModal]);

    // Re-fetch holidays if the user picks a date in a different year
    useEffect(() => {
        if (form.from_date) {
            const year = new Date(form.from_date).getFullYear();
            fetchHolidays(year);
        }
    }, [form.from_date ? new Date(form.from_date).getFullYear() : null]);

    useEffect(() => {
        fetchStats();
    }, [filters.startDate, filters.endDate]);

    const fetchTypes = async () => {
        try {
            const res = await api.get('/hrms/leave-types');
            if (res.data.status) setTypes(res.data.data);
        } catch (e) { }
    };

    const fetchEmployees = async () => {
        try {
            const res = await api.get('/hrms/employee-names');
            if (res.data.status) setEmployees(res.data.data);
        } catch (e) { }
    };

    const fetchStats = async () => {
        try {
            const todayStr = toInputDate(new Date());
            let from = filters.startDate;
            let to = filters.endDate;

            // If filter is set to default "Today", charts should show "This Month" stats
            if (from === todayStr && to === todayStr) {
                from = toInputDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
                to = toInputDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
            }

            const res = await api.get('/hrms/leave-stats', {
                params: { from_date: from, to_date: to }
            });
            if (res.data.status) {
                if (res.data.is_admin) {
                    setIsAdminStats(true);
                    setAdminStats(res.data.data);
                } else {
                    setIsAdminStats(false);
                    setStats(res.data.data);
                }
            }
        } catch (e) { }
    };

    const fetchPermissionAnalytics = async (year, month) => {
        try {
            const res = await api.get('/hrms/leave-analytics', {
                params: { year, month }
            });
            if (res.data.status) {
                const permissionHours = Number(res.data?.data?.stats?.permission_hours || 0);
                setPermissionAnalytics({ permission_hours: permissionHours });
                return permissionHours;
            }
        } catch (e) {
            setPermissionAnalytics({ permission_hours: 0 });
            return 0;
        }
        return 0;
    };

    const fetchPendingApprovals = async () => {
        try {
            const res = await api.get('/hrms/leave-approvals');
            if (res.data.status) setPendingApprovals(res.data.data);
        } catch (e) { }
    };

    const fetchLeaves = async () => {
        setLoading(true);
        try {
            let endpoint = '';
            // Decide endpoint based on Active Tab
            if (activeTab === 'history') {
                if (!canAccessHistoryTab) { setLoading(false); return; }
                endpoint = '/hrms/all-leaves-history';
            } else if (activeTab === 'approvals') {
                endpoint = '/hrms/leave-approvals';
            } else {
                endpoint = '/hrms/my-leaves';
            }

            const params = {
                leave_type_id: filters.type || '',
                employee_id: filters.employee_id || '',
                search: searchTerm,
                page: currentPage,
                per_page: itemsPerPage,
                sort_by: sortConfig.key,
                sort_dir: sortConfig.direction.toUpperCase()
            };

            // Send dates and status for all tabs except approvals (which shows all requests)
            if (activeTab !== 'approvals') {
                params.from_date = filters.startDate;
                params.to_date = filters.endDate;
            }
            
            params.status = filters.status !== 'All' ? filters.status : '';

            const res = await api.get(endpoint, { params });
            if (res.data.status) {

                setLeaves(res.data.data);
                if (res.data.meta) {
                    setMeta(res.data.meta);
                } else {
                    setMeta({ total: res.data.data.length, page: 1, per_page: itemsPerPage, last_page: 1 });
                }
            }

        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    // Update leaves when tab, filters, page or search changes
    useEffect(() => {
        fetchLeaves();
    }, [filters, activeTab, currentPage, itemsPerPage, searchTerm, sortConfig]);

    // Actions
    const handleApply = async () => {
        if (!form.from_date || !form.to_date) {
            toast.error('Please select leave dates');
            return;
        }
        if (isPermission) {
            if (!form.from_time || !form.to_time) {
                toast.error('Please select permission from and to time');
                return;
            }

            const requestedHours = getTimeDifferenceHours(form.from_time, form.to_time);
            if (requestedHours <= 0) {
                toast.error('Please select a valid permission time range');
                return;
            }

            checkPermissionAndSubmit();
            return;
        }
        if (isOD) {
            submitLeaveRequest();
            return;
        }


        //continues leave - sandwich rule

        // [MODIFIED] Sandwich Rule logic: Disabled as requested. 
        // Previously, this warned users if they applied leave adjacent to 3+ days of holidays.
        /*
        const preHolidays = getHolidayBlock(form.from_date);
        const postHolidays = getHolidayBlock(form.to_date);
        const totalAdjoinedHolidays = Array.from(new Set([...preHolidays, ...postHolidays])).length;

        if (totalAdjoinedHolidays >= 3) {
            const leaveDays = calculateNetDays(form.from_date, form.to_date);
            const totalImpact = totalAdjoinedHolidays + leaveDays;

            showConfirm(
                `Found ${totalAdjoinedHolidays} days long holiday adjacent to your leave. Applying this leave makes it ${totalImpact} days of consecutive absence, which will be counted as LOP. Continue?`,
                'Sandwich Rule Warning',
                checkLOPAndSubmit
            );
            return;
        }
        */

        checkLOPAndSubmit();
    };

    const getTimeDifferenceHours = (fromTime, toTime) => {
        if (!fromTime || !toTime) return 0;
        const start = new Date(`2000-01-01T${fromTime}`);
        const end = new Date(`2000-01-01T${toTime}`);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
        let diff = (end - start) / (1000 * 60 * 60);
        if (diff < 0) diff += 24;
        return Math.max(0, diff);
    };

    const checkPermissionAndSubmit = async () => {
        try {
            const requestedHours = getTimeDifferenceHours(form.from_time, form.to_time);
            const fromDate = new Date(form.from_date);
            const year = fromDate.getFullYear();
            const month = fromDate.getMonth() + 1;

            const currentPermissionHours = Number(await fetchPermissionAnalytics(year, month));
            const totalPermissionHours = currentPermissionHours + requestedHours;

            if (currentPermissionHours >= 3 || totalPermissionHours > 3) {
                showConfirm(
                    `Your monthly 3-hour permission limit reached. You have taken already ${currentPermissionHours.toFixed(1)} hours. Now applying ${requestedHours.toFixed(1)} hours, so it will be marked as 0.5 days LOP. Continue?`,
                    'Permission Limit Warning',
                    submitLeaveRequest
                );
                return;
            }

            submitLeaveRequest();
        } catch (e) {
            console.error('Error checking permission hours:', e);
            submitLeaveRequest();
        }
    };

    const checkLOPAndSubmit = async () => {
        try {
            // Check if this month's leave has already been taken
            // We use the projected balance based on current balance and future accruals
            // Use raw_leave_balance if available to correctly account for overdrawn/future-spent balances
            const baseBal = stats.hasOwnProperty('raw_leave_balance') ? Number(stats.raw_leave_balance) : Number(stats.leave_balance || 0);

            // Monthly accrual rule: 1 day per month
            const fromDate = new Date(form.from_date);
            const fromMonth = fromDate.getMonth() + 1;
            const fromYear = fromDate.getFullYear();

            const now = new Date();
            const nowMonth = now.getMonth() + 1;
            const nowYear = now.getFullYear();

            const monthsDiff = (fromYear - nowYear) * 12 + (fromMonth - nowMonth);
            const projectedBal = baseBal + Math.max(0, monthsDiff);
            const requestedDays = calculateNetDays(form.from_date, form.to_date, form.is_half_day);

            if (projectedBal < requestedDays) {
                const availableStr = projectedBal <= 0 ? "no paid days" : `${projectedBal.toFixed(1)} paid days`;
                const lopDays = projectedBal <= 0 ? requestedDays : (requestedDays - projectedBal);
                const actionStr = (lopDays >= requestedDays) ? "The entire" : `The remaining ${lopDays.toFixed(1)}`;

                showConfirm(
                    `Based on your current balance and future accruals, you will have ${availableStr} available for this period, but you are requesting ${requestedDays.toFixed(1)} days. ${actionStr} ${requestedDays > 1 ? 'days' : 'day'} will be marked as LOP. Continue?`,
                    'LOP Warning',
                    submitLeaveRequest
                );
                return;
            }

            submitLeaveRequest();
        } catch (e) {
            console.error('Error checking LOP:', e);
            submitLeaveRequest(); // Continue anyway if check fails
        }
    };

    const submitLeaveRequest = async () => {
        try {
            const res = await api.post('/hrms/apply-leave', form);
            if (res.data.status) {
                toast.success('Applied successfully');

                setShowModal(false);
                fetchStats();
                fetchLeaves();
                // Reset form
                setForm({
                    leave_type_id: '', from_date: today, to_date: today, from_time: '', to_time: '',
                    reason: '', is_emergency: false, compensate_date: '', late_reason: '',
                    alternate_arrangement: false, alternate_user_id: [], alternate_work_details: '',
                    is_half_day: false, half_day_timing: 'Forenoon'
                });
                setAltSearch('');
            }
        } catch (e) {
            // unexpected backend error, use Custom Popup
            showAlert(e.response?.data?.messages?.error || 'Failed to submit leave request', 'Submission Failed');
        }
    };

    const handleApprove = async (id) => {
        try {
            await api.post('/hrms/update-leave-status',
                { leave_id: id, action: 'Approve' },
                { params: { leave_id: id, action: 'Approve' } }
            );
            toast.success('Leave Approved');
            fetchLeaves();          // Refresh Table
            fetchPendingApprovals(); // Refresh Badge
            fetchStats();            // Refresh Charts
        } catch (e) { toast.error(e.response?.data?.messages?.error || 'Failed'); }
    };


    const confirmDelete = async () => {
        if (!deleteId) return;
        try {
            await api.post('/hrms/delete-leave', { leave_id: deleteId }, { params: { leave_id: deleteId } });
            toast.success('Deleted successfully');
            fetchLeaves();
            fetchStats();
            setShowDeleteModal(false);
            setDeleteId(null);
        } catch (e) {
            toast.error(e.response?.data?.messages?.error || 'Failed to delete');
            setShowDeleteModal(false);
        }
    };

    const handleDelete = (id) => {
        setDeleteId(id);
        setShowDeleteModal(true);
    };

    const handleRejectClick = (id) => {
        setSelectedLeaveId(id);
        setRejectReason('');
        setShowRejectModal(true);
    };

    const handleInquiryClick = (id, mode = 'Inquiry', existingThread = '') => {
        setCurrentInquiryId(id);
        setInquiryMode(mode);
        setCurrentInquiryThread(existingThread || '');
        setInquiryStatusNote('');
        setShowInquiryModal(true);
    };

    const confirmReject = async () => {
        if (!rejectReason.trim()) {
            toast.error('Please enter a rejection reason');
            return;
        }
        try {
            await api.post('/hrms/update-leave-status',
                { leave_id: selectedLeaveId, action: 'Reject', reason: rejectReason }
            );
            toast.success('Leave Rejected');
            fetchLeaves();
            fetchPendingApprovals();
            fetchStats();
            setShowRejectModal(false);
            setRejectReason('');
        } catch (e) { toast.error(e.response?.data?.messages?.error || 'Failed to reject'); }
    };

    const confirmInquiry = async () => {
        if (!inquiryStatusNote.trim()) {
            toast.error('Please provide a message for the inquiry');
            return;
        }
        try {
            const res = await api.post('/hrms/update-leave-status', {
                leave_id: currentInquiryId,
                action: inquiryMode,
                reason: inquiryStatusNote
            }, {
                params: {
                    leave_id: currentInquiryId,
                    action: inquiryMode
                }
            });

            if (res.data.status) {
                toast.success(res.data.message || (inquiryMode === 'InquiryReply' ? 'Reply sent successfully' : 'Inquiry sent successfully'));
                setShowInquiryModal(false);
                setInquiryStatusNote('');
                setCurrentInquiryId(null);
                setCurrentInquiryThread('');
                fetchLeaves();
                fetchPendingApprovals();
                fetchStats();
            }
        } catch (e) {
            toast.error(e.response?.data?.messages?.error || 'Failed to send inquiry');
        }
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <FiChevronDown className="opacity-20 group-hover:opacity-50 transition-opacity" />;
        return sortConfig.direction === 'asc' ? <FiChevronUp className="text-red-500 stroke-[3px]" /> : <FiChevronDown className="text-red-500 stroke-[3px]" />;
    };

    // Status Badge Helper
    const StatusBadge = ({ leave }) => {
        const { status, rejection_reason } = leave;
        let color = 'bg-gray-100 text-gray-600';
        let label = status;
        let icon = null;

        if (status === 'Approved') {
            color = 'bg-green-100 text-green-600';
            icon = <FiCheckCircle />;
        } else if (status === 'Rejected') {
            color = 'bg-red-100 text-red-600';
            icon = <FiXCircle />;
        } else if (status === 'Partially Approved' || status === 'Pending_Lead') {
            color = 'bg-yellow-100 text-yellow-600';
            label = 'Requested';
            icon = <FiClock />;
        } else if (status === 'Pending_MD') {
            color = 'bg-yellow-100 text-yellow-600';
            label = 'Waiting for MD';
            icon = <FiClock />;
        } else if (status && status.includes('Pending')) {
            color = 'bg-yellow-100 text-yellow-600';
            icon = <FiClock />;
        }

        return (
            <div className="flex flex-col gap-1">
                <span className={`px-2 py-1 rounded-full text-[11px] font-bold flex items-center gap-1 w-fit ${color} shadow-sm border border-current border-opacity-10`}>
                    {icon} {label}
                </span>
                {status === 'Rejected' && rejection_reason && (
                    <div className="text-[10px] text-red-500 font-bold max-w-[150px] leading-tight mt-1 bg-red-50 p-1 rounded border border-red-100 italic" title={rejection_reason}>
                        Reason: {rejection_reason}
                    </div>
                )}
            </div>
        );
    };

    // Toggle Selection for Multi-select
    const toggleAlternateUser = async (userId) => {
        let current = Array.isArray(form.alternate_user_id) ? [...form.alternate_user_id] : [];
        if (typeof current === 'string') current = [current];

        if (current.includes(userId)) {
            setForm({ ...form, alternate_user_id: current.filter(id => id !== userId) });
        } else {
            // Check availability for NEW addition
            if (!form.from_date || !form.to_date) {
                toast.error('Please select leave dates first');
                return;
            }
            try {
                const res = await api.get('/hrms/check-availability', {
                    params: { user_id: userId, from_date: form.from_date, to_date: form.to_date }
                });
                if (res.data.status) {
                    if (res.data.is_available) {
                        setForm({ ...form, alternate_user_id: [...current, userId] });
                        setAltSearch(''); // Clear search text on selection
                    } else {
                        const emp = Array.isArray(employees) ? employees.find(e => e.user_id == userId) : null;
                        const empName = emp ? `${emp.first_name} ${emp.last_name}` : 'Staff';
                        showAlert(`${empName} is on leave or unavailable during this period!`, 'Submission Failed');
                    }
                }
            } catch (e) {
                toast.error('Failed to check staff availability');
            }
        }
    };

    const handleExportExcel = async () => {
        try {
            const loadingToast = toast.loading('Preparing Excel...');

            // Fetch all records for export (ignoring pagination)
            let url = '/hrms/my-leaves';
            if (activeTab === 'history') url = '/hrms/all-leaves-history';
            if (activeTab === 'approvals') url = '/hrms/leave-approvals';

            const res = await api.get(url, {
                params: {
                    per_page: -1, // Fetch all for export
                    from_date: activeTab === 'approvals' ? '' : filters.startDate,
                    to_date: activeTab === 'approvals' ? '' : filters.endDate,
                    status: filters.status,
                    leave_type_id: filters.type,
                    employee_id: filters.employee_id,
                    search: searchTerm,
                    sort_by: 'from_date',
                    sort_dir: 'ASC'
                }
            });

            if (!res.data.status) throw new Error('Failed to fetch data');
            const dataToExport = res.data.data;

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Leaves');

            // 4. Headers definition first
            const showEmployeeColumn = activeTab === 'history' || activeTab === 'approvals';
            const headers = ['ID'];
            if (showEmployeeColumn) headers.push('Employee');
            headers.push('Applied On', 'Leave Type', 'From Date', 'To Date', 'Days', 'Reason');
            if (showEmployeeColumn) headers.push('Alternative Arrangement');
            headers.push('Status');

            const totalCols = headers.length;
            const colRange = String.fromCharCode(64 + totalCols);

            // 1. Title
            worksheet.mergeCells(`A1:${colRange}1`);
            const titleCell = worksheet.getCell('A1');
            titleCell.value = 'eNova Software and Hardware Solutions P Ltd';
            titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
            titleCell.font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFFF0000' } };

            // 2. SubTitle
            worksheet.mergeCells(`A2:${colRange}2`);
            const subTitleCell = worksheet.getCell('A2');
            subTitleCell.value = `Leave ${activeTab === 'approvals' ? 'Approvals' : activeTab === 'history' ? 'History' : 'Requests'}`;
            subTitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
            subTitleCell.font = { name: 'Arial', size: 12, bold: true };

            // 3. Date Range
            worksheet.mergeCells(`A3:${colRange}3`);
            const dateCell = worksheet.getCell('A3');
            dateCell.value = activeTab === 'approvals' ? 'Period: All' : `Period: ${filters.startDate} to ${filters.endDate}`;
            dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
            dateCell.font = { name: 'Arial', size: 10, bold: false };

            // Spacer
            worksheet.addRow([]);

            // 4. Headers Row
            const headerRow = worksheet.addRow(headers);

            headerRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFE5E7EB' } // Standard Light Gray
                };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });

            // 5. Data
            dataToExport.forEach(leave => {
                const days = Math.max(1, Math.ceil((new Date(leave.to_date) - new Date(leave.from_date)) / (1000 * 60 * 60 * 24) + 1));

                const formatDateTime = (date, time) => {
                    const formattedDate = formatDate(date);
                    if (!time || time === '00:00:00') return formattedDate;
                    const hrs = parseInt(time.split(':')[0]);
                    const mins = time.split(':')[1];
                    const ampm = hrs >= 12 ? 'PM' : 'AM';
                    const displayHrs = hrs % 12 || 12;
                    return `${date} (${displayHrs}:${mins} ${ampm})`;
                };

                const rowData = [
                    leave.id
                ];
                if (showEmployeeColumn) {
                    rowData.push(`${leave.first_name || ''} ${leave.last_name || ''}`.trim() || 'Me');
                }
                rowData.push(
                    formatDate(leave.created_at),
                    leave.leave_name,
                    formatDateTime(leave.from_date, leave.from_time),
                    formatDateTime(leave.to_date, leave.to_time),
                    days,
                    leave.reason
                );
                if (showEmployeeColumn) {
                    rowData.push(leave.alternate_names || '-');
                }
                rowData.push(leave.status);

                worksheet.addRow(rowData);
            });

            // 6. Column Widths
            const cols = [{ width: 8 }]; // ID
            if (showEmployeeColumn) cols.push({ width: 25 }); // Employee
            cols.push(
                { width: 15 }, // Applied On
                { width: 20 }, // Type
                { width: 15 }, // From
                { width: 15 }, // To
                { width: 8 },  // Days
                { width: 40 }  // Reason
            );
            if (showEmployeeColumn) cols.push({ width: 25 }); // Alternative Arrangement
            cols.push({ width: 15 }); // Status
            worksheet.columns = cols;

            // 7. Write and Save
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const fileName = `Leave_Report_${filters.startDate}_to_${filters.endDate}.xlsx`;
            saveAs(blob, fileName);

            toast.success('Excel Exported Successfully', { id: loadingToast });
        } catch (error) {
            console.error("Export Error:", error);
            toast.error("Failed to export Excel");
        }
    };

    return (
        <div className="space-y-6 animate-fade-in-up pb-10">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-extrabold text-gray-900">Leaves</h2>
                    <p className="text-gray-500 text-sm font-medium">Track staff activities, stats, and updates.</p>
                </div>
                <div className="flex items-center gap-3">
                    {(!canViewAllHistory || isHR) && (
                        <button onClick={() => {
                            setForm({
                                leave_type_id: '',
                                from_date: today,
                                to_date: today,
                                from_time: '',
                                to_time: '',
                                reason: '',
                                is_emergency: false,
                                compensate_date: '',
                                late_reason: '',
                                alternate_arrangement: false,
                                alternate_user_id: [],
                                alternate_work_details: '',
                                is_half_day: false,
                                half_day_timing: 'Forenoon'
                            });
                            setShowModal(true);
                        }} className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-bold shadow-md hover:bg-red-700 transition flex items-center gap-2">
                            <FiPlus /> Add Leave
                        </button>
                    )}
                </div>
            </div>

            {/* Stats Cards */}
            {isAdminStats ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Approval Status Chart */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col">
                        <div className="w-full flex items-center gap-2 mb-4">
                            <div className="w-1.5 h-6 bg-purple-600 rounded-full"></div>
                            <h3 className="font-bold text-gray-800">Approval Status</h3>
                        </div>
                        <div className="flex flex-col md:flex-row items-center justify-center w-full gap-4">
                            <div className="relative w-44 h-44 md:w-48 md:h-48">
                                <HighchartsReact
                                    highcharts={Highcharts}
                                    options={{
                                        chart: { type: 'pie', height: '100%' },
                                        title: {
                                            text: statusChartSelection ?
                                                `<div style="text-align:center"><span style="font-size:12px; color:#9ca3af; font-weight:500">${statusChartSelection.name}</span><br/><span style="font-size:24px; font-weight:bold; color:#1f2937">${statusChartSelection.y}</span></div>` :
                                                `<div style="text-align:center"><span style="font-size:12px; color:#9ca3af; font-weight:500">Total</span><br/><span style="font-size:24px; font-weight:bold; color:#1f2937">${adminStats.total_requests}</span></div>`,
                                            align: 'center', verticalAlign: 'middle', y: 15, useHTML: true
                                        },
                                        plotOptions: {
                                            pie: {
                                                innerSize: '75%',
                                                dataLabels: { enabled: false },
                                                borderWidth: 0,
                                                cursor: 'pointer',
                                                point: {
                                                    events: {
                                                        click: (e) => {
                                                            const name = e.point.name;
                                                            const statusMap = { 'Requested': 'Pending_Lead', 'Pending': 'Pending_MD', 'Approved': 'Approved', 'Rejected': 'Rejected' };
                                                            const newStatus = statusMap[name] || 'All';

                                                            if (statusChartSelection?.name === name) {
                                                                setStatusChartSelection(null);
                                                                setFilters(prev => ({ ...prev, status: 'All' }));
                                                                toast.success('Filter cleared');
                                                            } else {
                                                                setStatusChartSelection({ name, y: e.point.y });
                                                                setTypeChartSelection(null); // Clear other chart
                                                                setFilters(prev => ({
                                                                    ...prev,
                                                                    status: newStatus,
                                                                    type: '', // Clear other filter
                                                                    startDate: firstDayOfMonth,
                                                                    endDate: lastDayOfMonth
                                                                }));
                                                                if (canViewAllHistory) {
                                                                    setActiveTab('history');
                                                                    // Optional: scroll to list
                                                                    setTimeout(() => {
                                                                        document.getElementById('leave-list-section')?.scrollIntoView({ behavior: 'smooth' });
                                                                    }, 100);
                                                                }
                                                                toast.success(`Filtered by ${name} (This Month)`);
                                                            }
                                                        }
                                                    }
                                                },
                                                states: { hover: { halo: { size: 5 } } }
                                            }
                                        },
                                        tooltip: { pointFormat: '<b>{point.y}</b> requests' },
                                        series: [{
                                            name: 'Status',
                                            data: [
                                                { name: 'Requested', y: adminStats.requested_count, color: '#7C3AED' },
                                                { name: 'Approved', y: adminStats.approved_count, color: '#10B981' },
                                                { name: 'Pending', y: adminStats.pending_count, color: '#F59E0B' },
                                                { name: 'Rejected', y: adminStats.rejected_count, color: '#EF4444' }
                                            ]
                                        }],
                                        credits: { enabled: false }
                                    }}
                                />
                            </div>
                            <div className="flex flex-col gap-2 bg-gray-50/50 p-4 rounded-xl flex-1 max-w-[160px]">
                                {[
                                    { label: 'Requested', count: adminStats.requested_count, color: '#7C3AED' },
                                    { label: 'Approved', count: adminStats.approved_count, color: '#10B981' },
                                    { label: 'Pending', count: adminStats.pending_count, color: '#F59E0B' },
                                    { label: 'Rejected', count: adminStats.rejected_count, color: '#EF4444' }
                                ].map((item, i) => (
                                    <div key={i} className={`flex items-center justify-between gap-4 cursor-pointer hover:bg-white p-1.5 rounded-lg transition-colors ${statusChartSelection?.name === item.label ? 'bg-white shadow-sm ring-1 ring-purple-100' : ''}`}
                                        onClick={() => {
                                            const statusMap = { 'Requested': 'Pending_Lead', 'Pending': 'Pending_MD', 'Approved': 'Approved', 'Rejected': 'Rejected' };
                                            if (statusChartSelection?.name === item.label) {
                                                setStatusChartSelection(null);
                                                setFilters(prev => ({ ...prev, status: 'All' }));
                                            } else {
                                                setStatusChartSelection({ name: item.label, y: item.count });
                                                setTypeChartSelection(null); // Clear other chart
                                                setFilters(prev => ({ ...prev, status: statusMap[item.label], type: '', startDate: firstDayOfMonth, endDate: lastDayOfMonth }));
                                                if (canViewAllHistory) {
                                                    setActiveTab('history');
                                                    setTimeout(() => {
                                                        document.getElementById('leave-list-section')?.scrollIntoView({ behavior: 'smooth' });
                                                    }, 100);
                                                }
                                            }
                                        }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }}></div>
                                            <span className="text-xs font-bold text-gray-600">{item.label}</span>
                                        </div>
                                        <span className="text-xs font-extrabold text-gray-900">{item.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Leave Type Chart */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col">
                        <div className="w-full flex items-center gap-2 mb-4">
                            <div className="w-1.5 h-6 bg-purple-600 rounded-full"></div>
                            <h3 className="font-bold text-gray-800">Leave Type</h3>
                        </div>
                        <div className="flex flex-col md:flex-row items-center justify-center w-full gap-4">
                            <div className="relative w-44 h-44 md:w-48 md:h-48">
                                <HighchartsReact
                                    highcharts={Highcharts}
                                    options={{
                                        chart: { type: 'pie', height: '100%' },
                                        title: {
                                            text: typeChartSelection ?
                                                `<div style="text-align:center"><span style="font-size:12px; color:#9ca3af; font-weight:500">${typeChartSelection.name}</span><br/><span style="font-size:24px; font-weight:bold; color:#1f2937">${typeChartSelection.y}</span></div>` :
                                                `<div style="text-align:center"><span style="font-size:12px; color:#9ca3af; font-weight:500">Total Off</span><br/><span style="font-size:24px; font-weight:bold; color:#1f2937">${adminStats.total_time_off}</span></div>`,
                                            align: 'center', verticalAlign: 'middle', y: 0, useHTML: true
                                        },
                                        plotOptions: {
                                            pie: {
                                                innerSize: '75%',
                                                dataLabels: { enabled: false },
                                                borderWidth: 0,
                                                cursor: 'pointer',
                                                point: {
                                                    events: {
                                                        click: (e) => {
                                                            const name = e.point.name;
                                                            if (typeChartSelection?.name === name) {
                                                                setTypeChartSelection(null);
                                                                setFilters(prev => ({ ...prev, type: '' }));
                                                            } else {
                                                                const distItem = adminStats.type_dist.find(td => td.name === name);
                                                                setTypeChartSelection({ name, y: e.point.y });
                                                                setStatusChartSelection(null); // Clear other chart
                                                                if (distItem) {
                                                                    setFilters(prev => ({
                                                                        ...prev,
                                                                        type: distItem.leave_type_id,
                                                                        status: 'All', // Clear other filter
                                                                        startDate: firstDayOfMonth,
                                                                        endDate: lastDayOfMonth
                                                                    }));
                                                                    if (canViewAllHistory) {
                                                                        setActiveTab('history');
                                                                        setTimeout(() => {
                                                                            document.getElementById('leave-list-section')?.scrollIntoView({ behavior: 'smooth' });
                                                                        }, 100);
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        },
                                        series: [{
                                            name: 'Leave Type',
                                            data: adminStats.type_dist.map((td, idx) => ({
                                                name: td.name,
                                                y: td.count,
                                                color: ['#7C3AED', '#10B981', '#F59E0B', '#F472B6', '#60A5FA'][idx % 5]
                                            }))
                                        }],
                                        credits: { enabled: false }
                                    }}
                                />
                            </div>
                            <div className="flex flex-col gap-2 bg-gray-50/50 p-4 rounded-xl flex-1 max-w-[160px]">
                                {adminStats.type_dist.slice(0, 4).map((td, idx) => (
                                    <div key={idx} className={`flex items-center justify-between gap-3 cursor-pointer hover:bg-white p-1 rounded-lg transition-colors ${typeChartSelection?.name === td.name ? 'bg-white shadow-sm ring-1 ring-purple-100' : ''}`}
                                        onClick={() => {
                                            if (typeChartSelection?.name === td.name) {
                                                setTypeChartSelection(null);
                                                setFilters(prev => ({ ...prev, type: '' }));
                                            } else {
                                                setTypeChartSelection({ name: td.name, y: td.count });
                                                setStatusChartSelection(null); // Clear other chart
                                                setFilters(prev => ({ ...prev, type: td.leave_type_id, status: 'All', startDate: firstDayOfMonth, endDate: lastDayOfMonth }));
                                                if (canViewAllHistory) {
                                                    setActiveTab('history');
                                                    setTimeout(() => {
                                                        document.getElementById('leave-list-section')?.scrollIntoView({ behavior: 'smooth' });
                                                    }, 100);
                                                }
                                            }
                                        }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ['#7C3AED', '#10B981', '#F59E0B', '#F472B6', '#60A5FA'][idx % 5] }}></div>
                                            <span className="text-[10px] font-bold text-gray-600 truncate max-w-[80px]">{td.name}</span>
                                        </div>
                                        <span className="text-xs font-bold text-gray-700">{td.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
                    <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 ring-2 ring-blue-500 ring-opacity-20 flex flex-col justify-between">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-gray-500 text-sm font-medium text-blue-600">Accrued Leave Balance</p>
                            <FiInfo className="text-blue-500" />
                        </div>
                        <h3 className="text-4xl font-black text-blue-700">{parseFloat(stats.leave_balance || 0)}</h3>
                        <p className="text-[10px] text-gray-500 mt-1 flex items-center gap-1">
                            Accrued Leave Balance
                            {stats.unscheduled > 0 && (
                                <span className="bg-orange-100 text-orange-700 px-1 rounded ml-auto font-bold">
                                    {stats.unscheduled} Emergency
                                </span>
                            )}
                        </p>
                    </div>

                    <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-gray-500 text-sm font-medium text-amber-600">Leave Taken</p>
                            <FiClock className="text-amber-500" />
                        </div>
                        <h3 className="text-3xl font-bold text-gray-800">{parseFloat(stats.leaves_taken || 0)}</h3>
                        <p className="text-[10px] text-gray-400 mt-1">Total approved days</p>
                    </div>

                    <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-gray-500 text-sm font-medium text-green-600">Paid Days</p>
                            <FiCheckCircle className="text-green-500" />
                        </div>
                        <h3 className="text-3xl font-bold text-green-600">{parseFloat(stats.paid_leaves || 0)}</h3>
                        <p className="text-[10px] text-gray-400 mt-1">Consumed from balance</p>
                    </div>

                    <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-gray-500 text-sm font-medium text-red-600">LOP Days</p>
                            <FiXCircle className="text-red-500" />
                        </div>
                        <h3 className="text-3xl font-bold text-red-600">{parseFloat(stats.lop_leaves || 0)}</h3>
                        <p className="text-[10px] text-gray-400 mt-1">Loss of Pay</p>
                    </div>
                </div>
            )}



            {/* Tabs */}
            <div id="leave-list-section" className="flex flex-wrap items-center gap-2 border-b border-gray-200 pb-px">
                {/* My Leaves tab - hidden for admin since they use Leave History instead */}
                {!isAdmin && (
                    <button
                        onClick={() => setActiveTab('my_leaves')}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-t-xl font-bold transition-all relative ${activeTab === 'my_leaves' ? 'bg-white text-red-600 border border-gray-200 border-b-white -mb-px' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                    >
                        <FiFileText /> My Leaves
                    </button>
                )}

                <button
                    onClick={() => setActiveTab('approvals')}
                    className={`flex items-center gap-2 px-6 py-2.5 rounded-t-xl font-bold transition-all relative ${activeTab === 'approvals' ? 'bg-white text-red-600 border border-gray-200 border-b-white -mb-px' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                >
                    <FiCheckCircle /> Approvals
                    {stats.pending_approvals > 0 && (
                        <span className="ml-1.5 bg-red-600 text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">
                            {stats.pending_approvals}
                        </span>
                    )}
                </button>

                {canAccessHistoryTab && (
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-t-xl font-bold transition-all relative ${activeTab === 'history' ? 'bg-white text-red-600 border border-gray-200 border-b-white -mb-px' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                    >
                        <FiCalendar /> Leave History
                    </button>
                )}
            </div>

            {/* List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-5 border-b border-gray-100 space-y-4">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div className="flex items-center gap-4">
                            <h3 className="font-extrabold text-lg text-gray-800">
                                {activeTab === 'approvals' ? (isAdmin ? 'Pending Approvals' : 'Leave Approvals') : activeTab === 'history' ? (canViewAllHistory ? 'All Leave History' : 'Approval History') : 'My Leave History'}
                            </h3>
                        </div>

                        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                            {/* Modern Search */}
                            <div className="relative flex-1 md:w-64">
                                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search employee..."
                                    className="pl-9 pr-12 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:border-red-500 w-full transition-all shadow-sm"
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                />
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400 bg-white border border-gray-200 px-1 rounded shadow-sm hidden sm:block">
                                    &#8984;/
                                </div>
                            </div>

                            {/* Export Button */}
                            <button
                                onClick={handleExportExcel}
                                className="px-4 py-2.5 bg-white border border-gray-200 text-gray-600 rounded-xl text-sm font-bold hover:bg-gray-50 transition shadow-sm flex items-center gap-2"
                            >
                                <FiFileText className="text-red-500" /> Export
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:flex md:flex-wrap items-center gap-2 md:gap-3 p-2 md:p-4 bg-gray-50 rounded-2xl border border-gray-100">
                        {/* Date Range Filter */}
                            <div className="col-span-2 md:w-auto flex items-center justify-center md:justify-start gap-1 bg-white border border-gray-200 rounded-xl px-2 py-2 shadow-sm w-full md:w-auto overflow-visible h-auto">
                                <button
                                    onClick={() => {
                                        const d = new Date(filters.startDate);
                                        d.setMonth(d.getMonth() - 1);
                                        const first = toInputDate(new Date(d.getFullYear(), d.getMonth(), 1));
                                        const last = toInputDate(new Date(d.getFullYear(), d.getMonth() + 1, 0));
                                        setFilters({ ...filters, startDate: first, endDate: last });
                                    }}
                                    className="p-1.5 hover:bg-gray-50 rounded-lg text-gray-400 flex-shrink-0"
                                >
                                    <FiChevronLeft size={16} />
                                </button>
                                <div className="flex items-center justify-center gap-1 px-1">
                                    <input
                                        type="date"
                                        className="bg-transparent border border-transparent hover:border-gray-200 rounded-lg text-xs font-bold text-gray-700 outline-none focus:border-red-500 focus:bg-white h-7 text-center px-1 transition-all cursor-pointer uppercase tracking-tight w-28"
                                        value={filters.startDate}
                                        onChange={e => setFilters({ ...filters, startDate: e.target.value })}
                                        style={{ colorScheme: 'light' }}
                                    />
                                    <div className="w-px h-3 bg-gray-300 flex-shrink-0 rounded-full"></div>
                                    <input
                                        type="date"
                                        className="bg-transparent border border-transparent hover:border-gray-200 rounded-lg text-xs font-bold text-gray-700 outline-none focus:border-red-500 focus:bg-white h-7 text-center px-1 transition-all cursor-pointer uppercase tracking-tight w-28"
                                        value={filters.endDate}
                                        onChange={e => setFilters({ ...filters, endDate: e.target.value })}
                                        style={{ colorScheme: 'light' }}
                                    />
                                </div>
                                <button
                                    onClick={() => {
                                        const d = new Date(filters.startDate);
                                        d.setMonth(d.getMonth() + 1);
                                        const first = toInputDate(new Date(d.getFullYear(), d.getMonth(), 1));
                                        const last = toInputDate(new Date(d.getFullYear(), d.getMonth() + 1, 0));
                                        setFilters({ ...filters, startDate: first, endDate: last });
                                    }}
                                    className="p-1.5 hover:bg-gray-50 rounded-lg text-gray-400 flex-shrink-0"
                                >
                                    <FiChevronRight size={16} />
                                </button>
                            </div>
                        {/* Status Dropdown Filter */}
                            <div className="col-span-1 w-full md:w-auto relative">
                                <select
                                    className="w-full appearance-none bg-white border border-gray-200 text-gray-700 text-xs font-bold rounded-xl outline-none pl-3 pr-8 py-2.5 min-w-[0] md:min-w-[120px] focus:border-red-500 shadow-sm"
                                    value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}
                                >
                                    <option value="All">All Status</option>
                                    <option value="Approved">Approved</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Rejected">Rejected</option>
                                </select>
                            </div>

                        {/* Staff Dropdown */}
                        {activeTab === 'history' && (
                            <div className="col-span-1 w-full md:w-auto relative">
                                <select
                                    className="w-full appearance-none bg-white border border-gray-200 text-gray-700 text-xs font-bold rounded-xl outline-none pl-3 pr-8 py-2.5 min-w-[0] md:min-w-[140px] focus:border-red-500 shadow-sm"
                                    value={filters.employee_id} onChange={e => setFilters({ ...filters, employee_id: e.target.value })}
                                >
                                    <option value="">All Staffs</option>
                                    {employees.map(emp => (
                                        <option key={emp.user_id} value={emp.user_id}>{emp.first_name} {emp.last_name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Type Dropdown */}
                        <div className="col-span-1 w-full md:w-auto relative">
                            <select
                                className="w-full appearance-none bg-white border border-gray-200 text-gray-700 text-xs font-bold rounded-xl outline-none pl-3 pr-8 py-2.5 min-w-[0] md:min-w-[120px] focus:border-red-500 shadow-sm"
                                value={filters.type} onChange={e => setFilters({ ...filters, type: e.target.value })}
                            >
                                <option value="">All Types</option>
                                {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </div>

                        {/* Clear Filters */}
                        <button
                            onClick={() => {
                                setFilters({
                                    startDate: firstDayOfMonth,
                                    endDate: lastDayOfMonth,
                                    status: 'All',
                                    type: '',
                                    employee_id: ''
                                });
                                setSearchTerm('');
                            }}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-500 p-2.5 rounded-xl transition-colors shadow-sm w-fit"
                            title="Reset Filters"
                        >
                            <FiRefreshCw size={16} />
                        </button>
                    </div>
                </div>

                {/* Data Table Header with Page Size Selection */}
                <div className="p-4 border-b border-gray-100 bg-white flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 font-bold">Show</span>
                        <select
                            className="bg-white border border-gray-200 text-gray-700 text-xs font-bold rounded-lg outline-none pl-3 pr-4 py-1.5 focus:border-red-500 shadow-sm cursor-pointer hover:border-red-300 transition-all min-w-[65px] h-8 appearance-none"
                            value={itemsPerPage}
                            onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                        >
                            {[5, 10, 25, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
                            <option value={-1}>All</option>
                        </select>
                        <span className="text-xs text-gray-500 font-bold">entries</span>
                    </div>
                    {searchTerm && (
                        <div className="text-xs text-gray-400">
                            Found <span className="text-red-600 font-bold">{meta.total}</span> entries
                        </div>
                    )}
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-gray-500">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-y border-gray-100">
                            <tr>
                                {(activeTab === 'history' || activeTab === 'approvals') && (
                                    <th className="px-3 py-4 cursor-pointer group hover:bg-gray-100 transition-colors whitespace-nowrap" onClick={() => handleSort('employee')}>
                                        <div className="flex items-center gap-2">Employee {getSortIcon('employee')}</div>
                                    </th>
                                )}
                                <th className="px-3 py-4 cursor-pointer group hover:bg-gray-100 transition-colors whitespace-nowrap" onClick={() => handleSort('created_at')}>
                                    <div className="flex items-center gap-2">Applied On {getSortIcon('created_at')}</div>
                                </th>
                                <th className="px-3 py-4 cursor-pointer group hover:bg-gray-100 transition-colors whitespace-nowrap" onClick={() => handleSort('leave_type')}>
                                    <div className="flex items-center gap-2">Leave Type {getSortIcon('leave_type')}</div>
                                </th>
                                <th className="px-3 py-4 cursor-pointer group hover:bg-gray-100 transition-colors whitespace-nowrap" onClick={() => handleSort('from_date')}>
                                    <div className="flex items-center gap-2">Date & Time {getSortIcon('from_date')}</div>
                                </th>
                                <th className="px-3 py-4 whitespace-nowrap">Duration & Details</th>
                                <th className="px-3 py-4 min-w-[200px]">Reason</th>
                                <th className="px-3 py-4 whitespace-nowrap">Inquiry / Notes</th>
                                <th className="px-2 py-4 whitespace-nowrap">Alternate Arrangement</th>
                                <th className="px-3 py-4 cursor-pointer group hover:bg-gray-100 transition-colors whitespace-nowrap" onClick={() => handleSort('status')}>
                                    <div className="flex items-center gap-2">Status {getSortIcon('status')}</div>
                                </th>
                                <th className="px-3 py-4 text-right whitespace-nowrap">Action</th>
                            </tr>
                        </thead>
                        <tbody className={`divide-y divide-gray-100 ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
                            {paginatedLeaves.length === 0 && !loading ? (
                                <tr><td colSpan="8" className="text-center py-6">No records found</td></tr>
                            ) : (
                                paginatedLeaves.map(leave => {
                                    const isLOP = parseFloat(leave.lop_days || 0) > 0 && leave.status === 'Approved';
                                    return (
                                        <tr key={leave.id} className={`${isLOP ? 'bg-red-50 hover:bg-red-100' : 'bg-white hover:bg-gray-50'} transition-colors`}>
                                            {(activeTab === 'history' || activeTab === 'approvals') && (
                                                <td className="px-6 py-4 font-medium text-gray-900 align-middle">
                                                    <div className="flex items-center gap-3">
                                                        <img className="w-8 h-8 rounded-full" src={`https://ui-avatars.com/api/?name=${leave.first_name}`} alt="" />
                                                        <div>
                                                            <div className="font-bold">{leave.first_name} {leave.last_name}</div>
                                                            <div className="text-xs text-gray-500">{leave.designation_name || 'Employee'}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                            )}

                                            <td className="px-6 py-4 whitespace-nowrap align-middle">
                                                <div className="text-xs font-bold text-gray-600">
                                                    {new Date(leave.created_at).toDateString() === new Date().toDateString() ? (
                                                        <span className="text-red-600">Today</span>
                                                    ) : (
                                                        formatDate(leave.created_at)
                                                    )}
                                                </div>
                                                <div className="text-[10px] text-gray-400">
                                                    {new Date(leave.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </td>

                                            <td className="px-6 py-4 text-blue-600 font-medium align-middle">
                                                {leave.leave_name}
                                                {leave.unscheduled == 1 && (
                                                    <span className="ml-2 bg-red-100 text-red-600 text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider">Emergency</span>
                                                )}
                                            </td>

                                            <td className="px-6 py-4 align-middle">
                                                <div className="text-gray-800 font-medium">
                                                    {formatDate(leave.from_date)}
                                                    {leave.from_time && leave.from_time !== '00:00:00' && (
                                                        <span className="text-xs text-gray-500 ml-1">({formatTime(leave.from_time)})</span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-400 my-0.5">to</div>
                                                <div className="text-gray-800 font-medium">
                                                    {formatDate(leave.to_date)}
                                                    {leave.to_time && leave.to_time !== '00:00:00' && (
                                                        <span className="text-xs text-gray-500 ml-1">({formatTime(leave.to_time)})</span>
                                                    )}
                                                </div>
                                            </td>

                                            <td className="px-6 py-4 align-middle">
                                                <div className="font-bold flex flex-col">
                                                    <span>
                                                        {((leave.leave_name === 'Permission' || leave.code === 'PER') && (leave.from_time && leave.from_time !== '00:00:00' && leave.to_time && leave.to_time !== '00:00:00')) ? (
                                                            (() => {
                                                                const start = new Date(`2000-01-01T${leave.from_time}`);
                                                                const end = new Date(`2000-01-01T${leave.to_time}`);
                                                                let diffMs = end - start;
                                                                if (diffMs < 0) diffMs += 24 * 60 * 60 * 1000;
                                                                const minutes = Math.round(diffMs / (1000 * 60));
                                                                const h = Math.floor(minutes / 60);
                                                                const m = minutes % 60;
                                                                if (h === 0) return `${m} Min`;
                                                                if (m === 0) return `${h} ${h === 1 ? 'Hour' : 'Hours'}`;
                                                                return `${h} ${h === 1 ? 'Hr' : 'Hrs'} ${m} Min`;
                                                            })()
                                                        ) : (
                                                            <>
                                                                {(() => { const s = parseFloat(leave.total_days || 0); const d = s > 0 ? s : calculateNetDays(leave.from_date, leave.to_date, leave.is_half_day == 1); return <>{d}{d === 1 ? ' Day' : ' Days'}</>; })()}

                                                                {leave.is_half_day == 1 && <span className="text-[10px] text-orange-600 block">({leave.half_day_timing || 'Forenoon'})</span>}
                                                            </>
                                                        )}
                                                    </span>
                                                    {(leave.status === 'Approved' || leave.status === 'Partially Approved') && (
                                                        <div className="flex gap-1 text-[10px] mt-1 flex-wrap">
                                                            <span className="text-green-600 bg-green-50 px-1 rounded border border-green-100">P: {parseFloat(leave.paid_days || 0)}</span>
                                                            <span className="text-red-600 bg-red-50 px-1 rounded border border-red-100">L: {parseFloat(leave.lop_days || 0)}</span>
                                                            {parseFloat(leave.od_days || 0) > 0 && (
                                                                <span className="text-purple-600 bg-purple-50 px-1 rounded border border-purple-100">OD: {parseFloat(leave.od_days || 0)}</span>
                                                            )}
                                                            {leave.has_manual_override && (
                                                                <span className="text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded-full font-extrabold border border-orange-200 flex items-center gap-1 shadow-sm" title="HR Marked as Present/Leave manually for some days">
                                                                    Manual Override <FiInfo size={10} />
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Date-wise Status */}
                                                    {leave.individual_dates && leave.individual_dates.length > 0 && (
                                                        <div className="mt-2 space-y-1.5 max-h-[120px] overflow-y-auto pr-1 thin-scrollbar">
                                                            {leave.individual_dates.map(idate => (
                                                                <div key={idate.id} className="text-[10px] flex items-center justify-between gap-3 bg-white p-1 rounded-lg border border-gray-100 shadow-sm transition-all hover:border-gray-300">
                                                                    <div className="flex border-r border-gray-100 pr-2 min-w-[35px] flex-col items-center">
                                                                        <span className="text-gray-900 font-black leading-none">{new Date(idate.leave_date).getDate()}</span>
                                                                        <span className="text-gray-400 font-bold uppercase text-[7px] leading-none mt-0.5">{new Date(idate.leave_date).toLocaleString('en-GB', { month: 'short' })}</span>
                                                                    </div>
                                                                    <div className="flex-1 flex items-center justify-between gap-1">
                                                                        <span className={`font-extrabold px-1.5 py-0.5 rounded-md text-[9px] ${idate.status === 'Approved' ? 'text-green-600 bg-green-50' :
                                                                            idate.status === 'Rejected' ? 'text-red-600 bg-red-50' :
                                                                                idate.status === 'Cancelled' ? 'text-gray-400 bg-gray-50' :
                                                                                    'text-orange-600 bg-orange-50'
                                                                            }`}>
                                                                            {idate.status}
                                                                        </span>
                                                                        {idate.override_reason && (
                                                                            <FiInfo className="text-orange-500 flex-shrink-0" size={12} title={idate.override_reason} />
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>

                                            <td className="px-6 py-4 max-w-xs whitespace-normal break-words align-middle" title={leave.reason}>{leave.reason}</td>

                                            <td className="px-6 py-4 max-w-xs whitespace-normal break-words align-middle" title={leave.inquiry_note}>
                                                {leave.inquiry_note ? (
                                                    <div className="p-2 bg-blue-50 border-l-2 border-blue-500 text-[11px] text-blue-700 italic whitespace-pre-line">
                                                        <span className="font-bold block not-italic mb-0.5 text-blue-800 uppercase text-[9px]">Query/Instruction:</span>
                                                        {leave.inquiry_note}
                                                        {leave.inquiry_by_name && (
                                                            <span className="block not-italic mt-1 text-[9px] text-blue-600 font-semibold">— {leave.inquiry_by_name}</span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-300 italic text-[10px]">No inquiries</span>
                                                )}
                                            </td>

                                            <td className="px-2 py-4 text-xs align-middle">
                                                {leave.alternate_names ? (
                                                    <div>
                                                        <div className="font-bold text-gray-700 mb-1">Backup: {leave.alternate_names}</div>
                                                        {leave.alternate_work_details && (
                                                            <div className="text-gray-500 italic truncate max-w-[150px]" title={leave.alternate_work_details}>
                                                                "{leave.alternate_work_details}"
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </td>

                                            <td className="px-6 py-4 align-middle">
                                                <StatusBadge leave={leave} />
                                                {leave.approval_flow ? (
                                                    <div className="text-[10px] text-gray-500 mt-1 max-w-[150px] leading-tight font-medium italic">
                                                        {leave.approval_flow}
                                                    </div>
                                                ) : (
                                                    leave.waiting_for && <div className="text-xs text-gray-400 mt-1">Wait: {leave.waiting_for}</div>
                                                )}
                                            </td>

                                            <td className="px-6 py-4 text-right align-middle">
                                                {activeTab === 'approvals' && leave.can_approve && (
                                                    <div className="flex flex-col items-end gap-1.5">
                                                        <button onClick={() => handleApprove(leave.id)} className="bg-green-50 text-green-600 px-3 py-1 rounded-md text-xs font-bold hover:bg-green-100 border border-green-200 w-full text-center" title="Approve Leave">Approve</button>
                                                        <button onClick={() => handleInquiryClick(leave.id, 'Inquiry', leave.inquiry_note)} className="bg-blue-50 text-blue-600 px-3 py-1 rounded-md text-xs font-bold hover:bg-blue-100 border border-blue-200 w-full text-center" title="Send Inquiry/Instruction">Inquiry</button>
                                                        <button onClick={() => handleRejectClick(leave.id)} className="bg-red-50 text-red-600 px-3 py-1 rounded-md text-xs font-bold hover:bg-red-100 border border-red-200 w-full text-center" title="Reject Leave">Reject</button>
                                                    </div>
                                                )}
                                                {activeTab === 'history' && canViewAllHistory && isAdmin && (
                                                    <div className="flex justify-end">
                                                        <button onClick={() => handleDelete(leave.id)} className="text-gray-400 hover:text-red-600 transition-colors p-1 rounded-full hover:bg-red-50" title="Delete Permanent"><FiTrash2 size={18} /></button>
                                                    </div>
                                                )}
                                                {activeTab === 'my_leaves' && (
                                                    <div className="flex justify-end items-center gap-2">
                                                        {leave.inquiry_note && (
                                                            <button
                                                                onClick={() => handleInquiryClick(leave.id, 'InquiryReply', leave.inquiry_note)}
                                                                className="bg-blue-50 text-blue-600 px-3 py-1 rounded-md text-xs font-bold hover:bg-blue-100 border border-blue-200"
                                                                title="Reply to Inquiry"
                                                            >
                                                                Reply
                                                            </button>
                                                        )}
                                                        {leave.status === 'Pending_Lead' ? (
                                                            <>
                                                                <button onClick={() => {
                                                                    const altIds = (leave.alternate_user_id && leave.alternate_user_id !== '0' && leave.alternate_user_id !== '')
                                                                        ? (typeof leave.alternate_user_id === 'string' ? leave.alternate_user_id.split(',').map(Number) : leave.alternate_user_id)
                                                                        : [];
                                                                    setForm({
                                                                        id: leave.id,
                                                                        leave_type_id: leave.leave_type_id,
                                                                        from_date: leave.from_date,
                                                                        to_date: leave.to_date,
                                                                        from_time: leave.from_time,
                                                                        to_time: leave.to_time,
                                                                        reason: leave.reason.replace('[Emergency] ', ''),
                                                                        is_emergency: leave.reason.includes('[Emergency]'),
                                                                        compensate_date: leave.compensate_date,
                                                                        compensate_work_stats: leave.compensate_work_stats,
                                                                        late_reason: leave.late_reason,
                                                                        alternate_arrangement: altIds.length > 0,
                                                                        alternate_user_id: altIds,
                                                                        alternate_work_details: leave.alternate_work_details || '',
                                                                        is_half_day: leave.is_half_day == 1,
                                                                        half_day_timing: leave.half_day_timing || 'Forenoon'
                                                                    });
                                                                    setShowModal(true);
                                                                }} className="text-blue-500 hover:text-blue-700 p-1.5 rounded-full hover:bg-blue-50 transition-colors" title="Edit Request">
                                                                    <FiEdit size={16} />
                                                                </button>
                                                                <button onClick={() => handleDelete(leave.id)} className="text-red-400 hover:text-red-600 transition-colors p-1.5 rounded-full hover:bg-red-50" title="Cancel Request">
                                                                    <FiTrash2 size={16} />
                                                                </button>
                                                            </>
                                                        ) : leave.status === 'Approved' ? (
                                                            <div className="text-green-500 bg-green-50 p-1.5 rounded-full" title="Approved">
                                                                <FiCheckCircle size={18} />
                                                            </div>
                                                        ) : (
                                                            <span className="text-xs text-gray-400 italic">Locked</span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                {meta.total > 0 && (
                    <div className="flex flex-col sm:flex-row justify-between items-center p-4 border-t border-gray-100 bg-gray-50 gap-4">
                        <div className="text-sm text-gray-500 font-medium">
                            Showing <span className="font-bold text-gray-700">{meta.per_page === -1 ? 1 : (meta.page - 1) * meta.per_page + 1}</span> to <span className="font-bold text-gray-700">{meta.per_page === -1 ? meta.total : Math.min(meta.page * meta.per_page, meta.total)}</span> of <span className="font-bold text-gray-700">{meta.total}</span> entries
                        </div>

                        {meta.total > meta.per_page && meta.per_page !== -1 && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className={`px-4 py-1.5 text-sm rounded-md transition font-medium ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                                >
                                    Previous
                                </button>

                                {visiblePages.map(page => (
                                    <button
                                        key={page}
                                        onClick={() => setCurrentPage(page)}
                                        className={`w-8 h-8 flex items-center justify-center text-sm rounded-md transition font-bold ${page === currentPage ? 'bg-red-600 text-white shadow-md' : 'bg-white text-red-600 border border-red-500 hover:bg-red-50'}`}
                                    >
                                        {page}
                                    </button>
                                ))}

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className={`px-4 py-1.5 text-sm rounded-md transition font-medium ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200 border' : 'bg-white text-red-600 border border-red-500 hover:bg-red-50'}`}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Modal */}
            <AnimatePresence>
                {showModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4 backdrop-blur-sm overflow-y-auto">
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="bg-white rounded-xl shadow-2xl max-w-lg w-full font-sans my-10">
                            <div className="bg-red-600 p-4 flex justify-between items-center text-white rounded-t-xl">
                                <h3 className="font-bold text-lg">Leave Request Form</h3>
                                <button onClick={() => setShowModal(false)}><FiXCircle size={22} className="opacity-80 hover:opacity-100" /></button>
                            </div>

                            <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
                                {/* Leave Type Toggle */}
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Select Leave Type</label>
                                    <div className="flex gap-2 flex-wrap">
                                        {types.map(t => (
                                            <button
                                                key={t.id}
                                                onClick={() => {
                                                    const isHD = t.code === 'HD';
                                                    setForm(prev => ({
                                                        ...prev,
                                                        leave_type_id: t.id,
                                                        is_half_day: isHD,
                                                        to_date: isHD ? prev.from_date : prev.to_date
                                                    }));
                                                }}
                                                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${form.leave_type_id == t.id ? 'bg-red-600 border-red-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                                            >
                                                {t.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Dates */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-600 mb-1">From Date</label>
                                        <input type="date" className="w-full border border-gray-300 p-2 rounded-lg text-sm focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none transition-all"
                                            value={form.from_date}
                                            onFocus={(e) => {
                                                // Programmatically set min only when picking (blocks past dates in calendar)
                                                // This hides it from devtools when not active
                                                e.target.min = stats.min_date || new Date().toISOString().split('T')[0];
                                            }}
                                            onBlur={(e) => {
                                                // Remove min immediately after picking to hide from devtools
                                                e.target.removeAttribute('min');
                                            }}
                                            onChange={e => {
                                                const newDate = e.target.value;
                                                
                                                // --- AUTHORITATIVE FALLBACK VALIDATION (Hides min from initial console view) ---
                                                if (newDate && stats.min_date && newDate < stats.min_date) {
                                                    showAlert(`Cannot apply for past dates (Minimum Date: ${stats.min_date}).`, 'Invalid Date');
                                                    return;
                                                }

                                                const holidayCheck = isHoliday(newDate);
                                                if (holidayCheck.holiday) {
                                                    showAlert(`The selected date ${newDate} is a holiday (${holidayCheck.title}). Leave cannot be applied on holidays.`, 'Holiday Notification');
                                                    // Reset the date input if it's a holiday
                                                    setForm(prev => ({
                                                        ...prev,
                                                        from_date: '',
                                                        to_date: ''
                                                    })); 
                                                    return;
                                                }
                                                setForm(prev => ({
                                                    ...prev,
                                                    from_date: newDate,
                                                    to_date: newDate // Automatically sync to_date
                                                }));
                                            }} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-600 mb-1">To Date</label>
                                        <input type="date" className="w-full border border-gray-300 p-2 rounded-lg text-sm focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none transition-all"
                                            value={form.to_date}
                                            onFocus={(e) => {
                                                // Dynamic min for to_date based on from_date
                                                e.target.min = form.from_date || stats.min_date || new Date().toISOString().split('T')[0];
                                            }}
                                            onBlur={(e) => {
                                                e.target.removeAttribute('min');
                                            }}
                                            onChange={e => {
                                                const newDate = e.target.value;

                                                // --- AUTHORITATIVE FALLBACK VALIDATION ---
                                                const effectiveMin = form.from_date || stats.min_date;
                                                if (newDate && effectiveMin && newDate < effectiveMin) {
                                                    showAlert(`End date cannot be before ${effectiveMin}.`, 'Invalid Date');
                                                    return;
                                                }

                                                const holidayCheck = isHoliday(newDate);
                                                if (holidayCheck.holiday) {
                                                    showAlert(`The selected date ${newDate} is a holiday (${holidayCheck.title}). Leave cannot be applied on holidays.`, 'Holiday Notification');
                                                    // Reset the date input if it's a holiday
                                                    setForm(prev => ({
                                                        ...prev,
                                                        to_date: ''
                                                    }));
                                                    return;
                                                }
                                                setForm(prev => ({
                                                    ...prev,
                                                    to_date: newDate
                                                }));
                                            }} />
                                        {form.from_date && form.to_date && (
                                            <div className="mt-1 flex items-center gap-1.5">
                                                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                                                <span className="text-[11px] font-extrabold text-green-600 uppercase tracking-tight">
                                                    Selected Duration: {calculateNetDays(form.from_date, form.to_date, form.is_half_day)} {calculateNetDays(form.from_date, form.to_date, form.is_half_day) === 1 ? 'Day' : 'Days'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Half Day Toggle */}
                                {types.find(t => t.id == form.leave_type_id)?.code === 'HD' && (
                                    <div className="bg-red-50 p-3 rounded-lg border border-red-100 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-red-600 text-white shadow-md">
                                                <FiClock size={18} />
                                            </div>
                                            <div>
                                                <div className="text-sm font-bold text-gray-800">Half Day Leave</div>
                                                <div className="text-[10px] text-red-600 font-bold uppercase tracking-tight">Counts as 0.5 days</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <select
                                                className="bg-white border border-red-200 text-xs font-bold px-3 py-1.5 rounded-lg outline-none cursor-pointer focus:ring-1 focus:ring-red-600 shadow-sm"
                                                value={form.half_day_timing}
                                                onChange={e => setForm({ ...form, half_day_timing: e.target.value })}
                                            >
                                                {(() => {
                                                    const shiftStart = halfDayShift?.start_time?.slice(0, 5) || '09:15';
                                                    const splitTime  = halfDayShift?.half_day_split_time?.slice(0, 5) || '14:00';
                                                    const shiftEnd   = halfDayShift?.end_time?.slice(0, 5) || '18:00';
                                                    return (<>
                                                        <option value="Forenoon">Forenoon ({formatTime(shiftStart)} – {formatTime(splitTime)})</option>
                                                        <option value="Afternoon">Afternoon ({formatTime(splitTime)} – {formatTime(shiftEnd)})</option>
                                                    </>);
                                                })()}
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {/* Permission Timings */}
                                {isPermission && (
                                    <div className="grid grid-cols-2 gap-4 bg-yellow-50 p-3 rounded-lg border border-yellow-100">
                                        <div className="col-span-2 text-xs font-bold text-yellow-700 mb-1">Permission Timing (Hrs)</div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 mb-1">From Time</label>
                                            <input type="time" className="w-full border border-gray-300 p-2 rounded-lg text-sm"
                                                value={form.from_time} onChange={e => setForm({ ...form, from_time: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 mb-1">To Time</label>
                                            <input type="time" className="w-full border border-gray-300 p-2 rounded-lg text-sm"
                                                value={form.to_time} onChange={e => setForm({ ...form, to_time: e.target.value })} />
                                        </div>
                                    </div>
                                )}

                                {/* Compensate Info */}
                                {isCCL && (
                                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 space-y-3">
                                        <div className="text-xs font-bold text-blue-700">Compensatory Details</div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 mb-1">Worked On Date</label>
                                            <input type="date" className="w-full border border-gray-300 p-2 rounded-lg text-sm"
                                                value={form.compensate_date} onChange={e => setForm({ ...form, compensate_date: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 mb-1">Work Done Stats</label>
                                            <input type="text" className="w-full border border-gray-300 p-2 rounded-lg text-sm" placeholder="e.g. Completed Project X"
                                                value={form.compensate_work_stats} onChange={e => setForm({ ...form, compensate_work_stats: e.target.value })} />
                                        </div>
                                    </div>
                                )}

                                {/* Reason */}
                                <div>
                                    <label className="block text-sm font-bold text-gray-600 mb-1">Reason</label>
                                    <textarea className="w-full border border-gray-300 p-2 rounded-lg text-sm focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none transition-all" rows="3"
                                        value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })}></textarea>
                                </div>

                                {/* Late Reason */}
                                <div>
                                    <label className="block text-sm font-bold text-gray-600 mb-1">If Late Submission, State Reason (Optional)</label>
                                    <input type="text" className="w-full border border-gray-300 p-2 rounded-lg text-sm focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none transition-all"
                                        value={form.late_reason} onChange={e => setForm({ ...form, late_reason: e.target.value })} />
                                </div>

                                {/* Alternate Arrangements */}
                                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                    <div className="flex items-center justify-between mb-3">
                                        <label className="text-sm font-bold text-gray-700">Alternate Arrangements Made?</label>
                                        <div className="flex bg-white rounded-lg border border-gray-300 overflow-hidden">
                                            <button onClick={() => setForm({ ...form, alternate_arrangement: true })} className={`px-3 py-1 text-xs font-bold transition-colors ${form.alternate_arrangement ? 'bg-green-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>Yes</button>
                                            <button onClick={() => setForm({ ...form, alternate_arrangement: false })} className={`px-3 py-1 text-xs font-bold transition-colors ${!form.alternate_arrangement ? 'bg-red-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>No</button>
                                        </div>
                                    </div>

                                    {form.alternate_arrangement && (
                                        <div className="space-y-3 animate-fade-in-up">
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 mb-1">Select Alternate Staff (Searchable)</label>

                                                <div className="relative">
                                                    <div className="flex flex-wrap items-center gap-1 border border-gray-300 p-2 rounded-lg bg-white cursor-pointer focus-within:border-red-600 focus-within:ring-1 focus-within:ring-red-600 transition-all" onClick={() => setShowAltDropdown(true)}>
                                                        {Array.isArray(form.alternate_user_id) && form.alternate_user_id.map(uid => {
                                                            const emp = Array.isArray(employees) ? employees.find(e => e.user_id == uid) : null;
                                                            return emp ? (
                                                                <span key={uid} className="bg-gray-100 text-gray-700 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 border border-gray-200">
                                                                    {emp.first_name} {emp.last_name}
                                                                    <button onClick={(e) => { e.stopPropagation(); toggleAlternateUser(uid); }} className="hover:text-red-600"><FiXCircle /></button>
                                                                </span>
                                                            ) : null;
                                                        })}
                                                        <input
                                                            type="text"
                                                            className="outline-none text-sm flex-1 min-w-[100px] bg-transparent"
                                                            placeholder={form.alternate_user_id.length > 0 ? "" : "Search..."}
                                                            value={altSearch}
                                                            onChange={e => { setAltSearch(e.target.value); setShowAltDropdown(true); }}
                                                            onFocus={() => setShowAltDropdown(true)}
                                                        />
                                                    </div>

                                                    {showAltDropdown && (
                                                        <div className="absolute z-50 w-full bg-white shadow-xl border border-gray-200 rounded-lg max-h-48 overflow-y-auto mt-1" onMouseLeave={() => setShowAltDropdown(false)}>
                                                            {employees.filter(e => e.user_id != user.user_id && (e.first_name + ' ' + e.last_name).toLowerCase().includes(altSearch.toLowerCase())).map(emp => {
                                                                const isSelected = Array.isArray(form.alternate_user_id) && form.alternate_user_id.includes(emp.user_id);
                                                                return (
                                                                    <div key={emp.user_id}
                                                                        onClick={() => toggleAlternateUser(emp.user_id)}
                                                                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 flex items-center justify-between ${isSelected ? 'bg-red-600 text-white font-bold' : 'text-gray-700'}`}
                                                                    >
                                                                        {emp.first_name} {emp.last_name}
                                                                        {isSelected && <FiCheckCircle className="text-white" />}
                                                                    </div>
                                                                );
                                                            })}
                                                            {employees.filter(e => e.user_id != user.user_id && (e.first_name + ' ' + e.last_name).toLowerCase().includes(altSearch.toLowerCase())).length === 0 && (
                                                                <div className="p-3 text-sm text-gray-500">No matching employees</div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 mb-1">Work Given / Comments</label>
                                                <textarea className="w-full border border-gray-300 p-2 rounded-lg text-sm focus:border-red-600 outline-none" rows="2"
                                                    value={form.alternate_work_details} onChange={e => setForm({ ...form, alternate_work_details: e.target.value })}></textarea>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Emergency Toggle */}
                                <div className="flex items-center gap-2">
                                    <input type="checkbox" id="emergency" className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-600" checked={form.is_emergency} onChange={e => setForm({ ...form, is_emergency: e.target.checked })} />
                                    <label htmlFor="emergency" className="text-sm font-bold text-gray-600">
                                        Unscheduled Leave (If Emergency)
                                    </label>
                                </div>

                                <button onClick={handleApply} className="px-8 py-2 bg-red-600 text-white rounded-lg font-bold shadow-md hover:bg-red-700 hover:shadow-lg transition-all transform active:scale-[0.98] mx-auto block">
                                    Submit Leave Request
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Delete Confirmation Modal */}
            <AnimatePresence>
                {showDeleteModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[60] p-4 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 border border-red-100"
                        >
                            <div className="flex flex-col items-center text-center">
                                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-red-600 mb-4">
                                    <FiTrash2 size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Request?</h3>
                                <p className="text-gray-500 mb-6 text-sm">
                                    Are you sure you want to delete this leave request? This action cannot be undone.
                                </p>
                                <div className="flex gap-3 w-full">
                                    <button
                                        onClick={() => setShowDeleteModal(false)}
                                        className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-bold rounded-lg hover:bg-gray-200 transition"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={confirmDelete}
                                        className="flex-1 px-4 py-2 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 shadow-md hover:shadow-lg transition"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Rejection Modal */}
            <AnimatePresence>
                {showRejectModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[60] p-4 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6"
                        >
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Rejection Reason</h3>
                            <textarea
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="Enter reason for rejection..."
                                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-red-600 focus:border-red-600 outline-none h-32 resize-none mb-4"
                                autoFocus
                            />
                            <div className="flex gap-3 w-full">
                                <button
                                    onClick={() => setShowRejectModal(false)}
                                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-bold rounded-lg hover:bg-gray-200 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmReject}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 shadow-md hover:shadow-lg transition"
                                >
                                    Reject Request
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Inquiry Modal */}
            <AnimatePresence>
                {showInquiryModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[60] p-4 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 border border-red-100"
                        >
                            <h3 className="text-lg font-extrabold text-red-900 mb-2 flex items-center gap-2">
                                <FiInfo size={20} className="text-red-600" />
                                {inquiryMode === 'InquiryReply' ? 'Reply To Inquiry' : 'Leave Inquiry / Instructions'}
                            </h3>
                            <p className="text-xs text-red-700/60 mb-4 font-medium italic">
                                {inquiryMode === 'InquiryReply'
                                    ? 'Reply to the approver note here. This will not change the leave approval status.'
                                    : 'Type a short note or work instructions for the employee. This status will not change the approval state.'}
                            </p>
                            {currentInquiryThread && (
                                <div className="mb-4 p-3 rounded-lg border border-blue-100 bg-blue-50 text-xs text-blue-700 whitespace-pre-line max-h-32 overflow-y-auto">
                                    {currentInquiryThread}
                                </div>
                            )}
                            <textarea
                                value={inquiryStatusNote}
                                onChange={(e) => setInquiryStatusNote(e.target.value)}
                                placeholder={inquiryMode === 'InquiryReply' ? 'Type your reply here...' : 'Your leave will be approved once you complete your current task..'}
                                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-red-600 focus:border-red-600 outline-none h-32 resize-none mb-4"
                                autoFocus
                            />
                            <div className="flex gap-3 w-full">
                                <button
                                    onClick={() => setShowInquiryModal(false)}
                                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-bold rounded-lg hover:bg-gray-200 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmInquiry}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 shadow-md hover:shadow-lg transition"
                                >
                                    {inquiryMode === 'InquiryReply' ? 'Send Reply' : 'Send Note'}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Custom Alert/Confirm Modal */}
            <AnimatePresence>
                {alertConfig.show && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden border border-gray-100"
                        >
                            <div className="bg-red-600 p-4 text-white flex items-center gap-3">
                                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                                    <FiInfo size={20} />
                                </div>
                                <h3 className="font-bold text-lg">{alertConfig.title}</h3>
                            </div>
                            <div className="p-6">
                                <p className="text-gray-600 font-medium leading-relaxed">
                                    {alertConfig.message}
                                </p>
                            </div>
                            <div className="p-4 bg-gray-50 flex gap-3">
                                {alertConfig.cancelText && (
                                    <button
                                        onClick={alertConfig.onCancel}
                                        className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 font-bold rounded-xl hover:bg-white transition-all active:scale-95"
                                    >
                                        {alertConfig.cancelText}
                                    </button>
                                )}
                                <button
                                    onClick={alertConfig.onConfirm}
                                    className="flex-1 px-4 py-2.5 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 shadow-lg shadow-red-200 transition-all active:scale-95"
                                >
                                    {alertConfig.confirmText}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};


export default LeaveManagement;
